import React, { useState, useEffect } from 'react';
import { ExpertiseTabsProps } from '../../types/expertise-detail.types';
import { PropertyCard } from '../PropertyCard';
import { Documents } from '../Communication/Documents';
import { Chat } from '../Communication/Chat';
import { Reports } from '../Downloads/Reports';
import { Invoices } from '../Downloads/Invoices';
import { Loading } from '@/ui/components/atoms/Loading';

export const ExpertiseTabs: React.FC<ExpertiseTabsProps> = ({
  activeTab,
  onTabChange,
  expertise,
  affaireData,
}) => {
  const [isChangingTab, setIsChangingTab] = useState(false);

  // Handle tab change with loading state
  const handleTabChange = (tabId: string) => {
    if (tabId === activeTab) return;
    
    setIsChangingTab(true);
    onTabChange(tabId);
  };

  // Clear loading state after tab change
  useEffect(() => {
    if (isChangingTab) {
      const timer = setTimeout(() => {
        setIsChangingTab(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [activeTab, isChangingTab]);
  const tabs = [
    { 
      id: 'overview', 
      label: 'Vue d\'ensemble', 
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
          <rect x="14" y="3" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
          <rect x="3" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
          <rect x="14" y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    },
    { 
      id: 'documents', 
      label: 'Documents', 
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    { 
      id: 'messages', 
      label: 'Messages', 
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
  ];

  return (
    <div className="expertise-tabs">
      <div className="tabs-header">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
            aria-selected={activeTab === tab.id}
            role="tab"
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="tabs-content">
        {isChangingTab ? (
          <div className="tab-loading">
            <Loading size="medium" text="Chargement..." />
          </div>
        ) : (
          <>
        {activeTab === 'overview' && (
          <div className="tab-overview">
            <div className="overview-grid">
              {/* Property Section */}
              <div className="property-section">
                <PropertyCard 
                  properties={affaireData.biens}
                  affaireId={expertise.affaire_id}
                  loading={affaireData.loading}
                />
              </div>

              {/* Reports & Invoices Section */}
              <div className="downloads-section">
                <Reports 
                  affaireId={expertise.affaire_id}
                  reports={expertise.reports}
                />
                <Invoices 
                  affaireId={expertise.affaire_id}
                />
              </div>

              {/* Contacts Section */}
              <div className="contacts-section">
                <h3>Contacts</h3>
                <div className="contacts-grid">
                  {/* Expert Card - Using agent_traitant from biens */}
                  {affaireData.biens && affaireData.biens[0]?.agent_traitant && (
                    <div className="contact-card expert-contact">
                      <div className="contact-header">
                        <div className="contact-avatar expert-avatar">
                          {affaireData.biens[0].agent_traitant.split(' ').map((n: string) => n[0]).join('').substring(0, 2)}
                        </div>
                        <div className="contact-info">
                          <p className="contact-name">
                            {affaireData.biens[0].agent_traitant}
                          </p>
                          <div className="contact-roles">
                            <span className="role-badge expert-role">Agent traitant</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Other Contacts - Filter out expert to avoid duplicate */}
                  {affaireData.contacts && affaireData.contacts.length > 0 && 
                    affaireData.contacts
                      .filter((contact) => {
                        // Filter out the expert contact to avoid duplication
                        // Check by email first (most reliable)
                        if (expertise.expert?.email && contact.email_1 === expertise.expert.email) {
                          return false;
                        }
                        // Also check if contact has 'expert' role
                        if (contact.roles && contact.roles.some(role => role.toLowerCase() === 'expert')) {
                          return false;
                        }
                        return true;
                      })
                      .map((contact) => (
                      <div key={contact.k_id_contact} className="contact-card">
                        <div className="contact-header">
                          <div className="contact-avatar">
                            {contact.prenom[0]}{contact.nom_representant[0]}
                          </div>
                          <div className="contact-info">
                            <p className="contact-name">
                              {contact.prenom} {contact.nom_representant}
                            </p>
                            {contact.roles && contact.roles.length > 0 && (
                              <div className="contact-roles">
                                {contact.roles.map((role, index) => (
                                  <span key={index} className="role-badge">{role}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="contact-details">
                          <a href={`mailto:${contact.email_1}`} className="contact-link">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M22 6l-10 7L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            {contact.email_1}
                          </a>
                          <a href={`tel:${contact.tel_mobile}`} className="contact-link">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            {contact.tel_mobile}
                          </a>
                        </div>
                      </div>
                    ))
                  }
                  
                  {/* Empty state if no contacts at all */}
                  {(!affaireData.biens || !affaireData.biens[0]?.agent_traitant) && 
                   (!affaireData.contacts || affaireData.contacts.length === 0) && (
                    <div className="empty-state full-width">Aucun contact trouvé</div>
                  )}
                </div>
              </div>

            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="tab-documents">
            <Documents 
              affaireId={expertise.affaire_id}
              documents={affaireData.documents}
            />
          </div>
        )}

        {activeTab === 'messages' && (
          <div className="tab-messages">
            <Chat affaireId={expertise.affaire_id} />
          </div>
        )}
          </>
        )}
      </div>

      <style>{`
        .expertise-tabs {
          background: white;
          border-radius: 24px;
          overflow: hidden;
          margin-top: 32px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
          border: 1px solid #E5E7EB;
          position: relative;
        }
        
        .expertise-tabs::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          z-index: 2;
        }
        

        .tabs-header {
          display: flex;
          gap: 0;
          padding: 0;
          background: linear-gradient(180deg, #F8F9FA 0%, #F3F4F6 100%);
          border-bottom: 1px solid #E5E7EB;
          position: relative;
          z-index: 1;
        }

        .tab-button {
          flex: 1;
          padding: 20px 24px;
          background: transparent;
          border: none;
          cursor: pointer;
          font-size: 15px;
          font-weight: 600;
          color: #6B7280;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 0;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        
        .tab-button::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          right: 0;
          height: 3px;
          background: transparent;
          transition: background 0.3s ease;
        }

        .tab-button:hover {
          color: #002035;
          background: rgba(239, 83, 67, 0.05);
        }

        .tab-button.active {
          background: white;
          color: #002035;
          box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
        }
        
        .tab-button.active::after {
          background: #ef5343;
        }

        .tab-button.active .tab-icon svg {
          stroke-width: 2.5;
          color: #ef5343;
        }

        .tab-icon {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .tab-icon svg {
          transition: all 0.2s ease;
        }

        .tabs-content {
          padding: 32px;
          background: transparent;
          border-radius: 0;
          animation: fadeIn 0.3s ease;
          position: relative;
          z-index: 1;
          min-height: 500px;
        }

        .tab-loading {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 400px;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .overview-grid {
          display: flex;
          flex-direction: column;
          gap: 28px;
        }

        .property-section {
          margin-bottom: 8px;
        }

        .downloads-section {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .contacts-section {
          padding: 0;
          background: transparent;
          border-radius: 0;
          border: none;
          box-shadow: none;
        }


        .contacts-section h3 {
          font-size: 18px;
          font-weight: 700;
          color: #002035;
          margin: 24px 0 24px 0;
          display: flex;
          align-items: center;
          gap: 10px;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .contacts-section h3::before {
          content: '';
          width: 4px;
          height: 24px;
          background: #ef5343;
          border-radius: 2px;
        }

        h4 {
          font-size: 16px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 12px 0;
        }

        .placeholder-card,
        .reports-card,
        .invoices-card {
          padding: 20px;
          background: #F9FAFB;
          border: 1px solid #E5E7EB;
          border-radius: 12px;
        }

        .contacts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 16px;
        }

        .contact-card {
          padding: 20px;
          background: #ffffff;
          border: 1px solid #E5E7EB;
          border-radius: 16px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .contact-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #c75c5c 0%, #FBB040 100%);
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .contact-card:hover {
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
          transform: translateY(-4px);
          border-color: #ef5343;
        }

        .contact-card:hover::before {
          opacity: 1;
        }

        .contact-header {
          display: flex;
          gap: 12px;
          margin-bottom: 12px;
        }

        .contact-avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #002035 0%, #003554 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 14px;
          box-shadow: 0 2px 8px rgba(0, 32, 53, 0.2);
        }
        
        .contact-avatar.expert-avatar {
          background: linear-gradient(135deg, #10B981 0%, #059669 100%);
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
        }

        .contact-info {
          flex: 1;
        }

        .contact-name {
          font-weight: 700;
          color: #002035;
          margin: 0 0 6px 0;
          font-size: 15px;
        }

        .contact-roles {
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
        }

        .role-badge {
          padding: 3px 10px;
          background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
          color: #78350F;
          border: 1px solid #FBB040;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        
        .role-badge.expert-role {
          background: linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%);
          color: #065F46;
          border: 1px solid #10B981;
        }

        .contact-details {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .contact-link {
          color: #6B7280;
          text-decoration: none;
          font-size: 13px;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.2s ease;
          padding: 4px 0;
        }

        .contact-link:hover {
          color: #c75c5c;
          padding-left: 4px;
        }

        .reports-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .report-item {
          display: flex;
          justify-content: space-between;
          padding: 12px;
          background: white;
          border-radius: 6px;
        }

        .report-type {
          font-weight: 500;
          color: #374151;
        }

        .report-status {
          font-size: 14px;
        }

        .report-status.available {
          color: #10B981;
        }

        .report-status.unavailable {
          color: #9CA3AF;
        }


        .empty-state {
          padding: 60px 48px;
          text-align: center;
          color: #6B7280;
          background: #F8F9FA;
          border-radius: 16px;
          border: 2px dashed #E5E7EB;
          font-size: 14px;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          position: relative;
          overflow: hidden;
        }
        
        
        .empty-state.full-width {
          grid-column: 1 / -1;
        }

        .documents-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }

        .doc-section {
          background: #F9FAFB;
          padding: 20px;
          border-radius: 8px;
        }

        .doc-item {
          padding: 12px;
          background: white;
          border: 1px solid #E5E7EB;
          border-radius: 6px;
          margin-bottom: 8px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .doc-item.verified {
          border-color: #10B981;
          background: #F0FDF4;
        }

        .doc-name {
          font-weight: 500;
          color: #374151;
        }

        .doc-date {
          font-size: 13px;
          color: #6B7280;
        }

        @media (max-width: 1024px) {
          .downloads-section,
          .info-cards {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .expertise-tabs {
            border-radius: 16px;
            margin-top: 24px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
          }
          
          .tabs-header {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .tab-button {
            padding: 16px 16px;
            font-size: 14px;
            min-width: 120px;
          }
          
          .tabs-content {
            padding: 20px;
            min-height: 400px;
          }

          .tab-label {
            display: none;
          }

          .tab-icon svg {
            width: 20px;
            height: 20px;
          }

          .contacts-grid {
            grid-template-columns: 1fr;
          }

          .documents-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};